import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment/moment';
import { ProjectsService } from '../../../../core/projects.service';
import { AuthService } from '../../../../../../shared/services';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../state/app.state';
import { UpdateProjectDocumentsBreadcrumbs } from '../../../../core/projects.actions';
import { projectDetails } from '../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../shared/core/shared.selectors';
import { Ability, subject } from '@casl/ability';
import {
  isDiaryDetailMode,
} from '../../../site-details-page/tab-screens/daily-diary/daily-diary-mode.util';
import { isSampleCollectionEnabled } from '../../../../../../shared/helpers/sample-collection';
import { AnalyticsService } from '../../../../../../shared/services/analytics.service';
import { ReportService as RptService } from '../../../../../../shared/services/report.service';
import * as yaml from 'js-yaml';
import { Project } from '../../../../../../shared/models';
import { ISSUES_TAB, ISSUES_TAB_QUERY_PARAMS } from '../../../../../../shared/helpers/issues-tab';

/** Tabs always rendered inline before "More". */
const BASE_INLINE_TAB_COUNT = 4;
/**
 * Tabs that, when visible, widen the inline bar by one slot each (Issues,
 * Samples, MGVW), so they don't push Submittals / RFIs / Payapp into "More".
 */
const EXTRA_INLINE_TAB_IDS = [17, 12, 16];

/** Query params owned by the risk register list (risk-register-list.component.ts). */
const RISK_LIST_PARAMS_CLEARED = {
  riskId: null, status: null, owner: null, area: null, discipline: null, band: null, cell: null, search: null,
  internal: null, sort: null, dir: null, page: null,
};

@Component({
    selector: 'app-tab-group',
    templateUrl: './tab-group.component.html',
    styleUrls: ['./tab-group.component.scss'],
    standalone: false
})
export class TabGroupComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() isContractorMode: boolean;
  @Input() displayActivityCodes: boolean;
  @Input() currentUser: any;
  headersList: any[] = [
    {
      id: 0,
      label: 'Daily reports',
      qp: 'sites',
    },
    {
      id: 17,
      label: 'Issues',
      qp: 'issues',
    },
    {
      id: 15,
      label: 'Diaries',
      qp: 'diary',
    },
    {
      id: 12,
      label: 'Samples',
      qp: 'samples',
    },
    {
      id: 16,
      label: 'MGVW',
      qp: 'mgvw',
    },
    {
      id: 1,
      label: 'Submittals',
      qp: 'submittals',
    },
    {
      id: 2,
      label: 'RFIs',
      qp: 'rfis',
    },
    {
      id: 3,
      label: 'Payapp',
      qp: 'payapp',
    },
    {
      id: 4,
      label: 'Change orders',
      qp: 'mtco',
    },
    {
      id: 5,
      label: 'Activity ids',
      qp: 'activity-ids',
    },
    {
      id: 6,
      label: 'Consolidated reports',
      qp: 'consolidated-reports',
    },
    {
      id: 7,
      label: 'Documents',
      qp: 'documents',
    },
    {
      id: 19,
      label: 'Drawings',
      qp: 'drawings',
    },
    {
      id: 9,
      label: 'Delivery tracking',
      qp: 'delivery-tracking',
    },
    {
      id: 10,
      label: 'Offsite tests',
      qp: 'offsite-tests',
      navigateOut: true,
    },
    {
      id: 11,
      label: 'Warranties & Claims',
      qp: 'warranty',
    },
    {
      id: 13,
      label: 'Punch lists',
      // Plural like the other tabs (Sri 2026-10-12); analytics keeps the old tab_name
      analyticsName: 'Punchlists',
      qp: 'punchlist',
    },
    {
      id: 18,
      label: 'Risk register',
      qp: 'risks',
    },
    {
      id: 14,
      label: 'CDOT Forms',
      qp: 'forms',
    },
    {
      id: 8,
      label: 'Settings',
      qp: 'settings',
    },
  ];
  // inject() rather than a constructor param: the repo's lint flags
  // constructor injection, so a new dependency shouldn't add a violation.
  private readonly ability = inject(Ability);

  /**
   * Headers actually on the bar. The template slices this, NOT `headersList`:
   * slicing first and filtering inside the loop let a hidden entry consume an
   * inline slot, so a user without diary access saw three inline tabs instead
   * of four once Diaries was added.
   */
  get visibleHeaders(): { id: number; label: string; qp: string; display?: boolean }[] {
    return this.headersList.filter(header => header.display);
  }

  /**
   * Prefer four inline tabs plus enabled Issues / Samples / MGVW slots,
   * limited by the measured space left beside the header actions.
   */
  get inlineTabCount(): number {
    // These roles get no "More" menu, so every tab their whitelist allows sits inline.
    if (this.hidesMoreMenu) return this.visibleHeaders.length;
    const extra = this.headersList.filter(
      header => EXTRA_INLINE_TAB_IDS.includes(header.id) && header.display,
    ).length;
    const count = BASE_INLINE_TAB_COUNT + extra;
    return Math.min(count, this.fittingTabCount);
  }

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private fittingTabCount = Number.POSITIVE_INFINITY;
  private layoutFrame: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  ngAfterViewChecked() {
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleTabLayout());
    }
    // Actions can be replaced when switching between lists and record views.
    this.element.nativeElement.querySelectorAll('.header-section, .header-actions, .tab-measurements')
      .forEach(node => this.resizeObserver.observe(node));
    this.scheduleTabLayout();
  }

  private scheduleTabLayout() {
    if (this.destroyed || this.layoutFrame !== null) return;
    this.zone.runOutsideAngular(() => {
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.fitTabs();
      });
    });
  }

  private fitTabs() {
    if (this.hidesMoreMenu) return;
    const host = this.element.nativeElement;
    const row = host.querySelector<HTMLElement>('.header-section');
    const actions = host.querySelector<HTMLElement>('.header-actions');
    const measurements = Array.from(host.querySelectorAll<HTMLElement>('.tab-measurements .header'));
    const headers = this.visibleHeaders;
    if (!row || measurements.length !== headers.length + 1) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const actionsWidth = actions?.getBoundingClientRect().width || 0;
    const available = row.clientWidth - (actionsWidth ? actionsWidth + gap : 0);
    const selectedIndex = headers.findIndex(header => header.id === this.selectedTabId);
    const preferred = Math.min(headers.length, BASE_INLINE_TAB_COUNT + headers.filter(
      header => EXTRA_INLINE_TAB_IDS.includes(header.id),
    ).length);
    let count = preferred;
    for (; count > 0; count--) {
      // A selected overflow tab replaces "More"; reserve its full label width.
      const moreWidth = selectedIndex >= count
        ? measurements[selectedIndex].getBoundingClientRect().width + 21
        : measurements[headers.length].getBoundingClientRect().width + 21;
      const tabsWidth = measurements.slice(0, count).reduce(
        (width, tab) => width + tab.getBoundingClientRect().width, 0,
      );
      if (tabsWidth + moreWidth + gap * count <= available) break;
    }
    const label = selectedIndex >= count ? headers[selectedIndex]?.label : null;
    if (count !== this.fittingTabCount || label !== this.selectedTabLabel) {
      this.zone.run(() => {
        this.fittingTabCount = count;
        this.selectedTabLabel = label;
        this.changeDetector.markForCheck();
      });
    }
  }

  get hidesMoreMenu(): boolean {
    return this.actingRole === 'contractor-basic' || this.actingRole === 'reviewer';
  }

  /** Diary record view is open — the chips give way to its date strip. */
  diaryDetailMode = false;

  selectedTabId: number = null;
  selectedTabLabel: any;
  account: any;
  role: any;
  actingRole: any;
  projectId: string;
  changeOrderMode = 'direct';
  // Samples tab gate: org has `sample_record_general` under dailyReportFields
  // and, when the org rolls sample collection out per project, this project
  // is opted in. Mirrors the mobile app's gate (see isSampleCollectionEnabled).
  samplesEnabled = false;
  private project: any = null;
  private appData: any = null;
  // Org-level gate for the MGVW tab: the org has the `contract_mgvw` group
  // under dailyReportFields (the same flag the mobile app's tab uses).
  mgvwEnabled = false;
  // Diaries are an AASHTOWare document: the tab needs the org's integration
  // switch on top of the acl grant (org-admins hold diary read everywhere).
  aashtoEnabled = false;
  formsTabEnabled = false;
  private destroyed = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private store: Store<fromRoot.State>,
    private analyticsService: AnalyticsService,
    private rptService: RptService,
  ) {
  }

  /**
   * Whether this user can see any diary on this project. The acl registers
   * DailyDiary rules per project for AASHTOWare-eligible roles only, so users
   * without one get no Diary tab at all.
   */
  canReadDiaries(): boolean {
    if (!this.projectId) return false;
    return this.ability.can('read', subject('DailyDiary', { project_id: this.projectId }));
  }

  /**
   * Samples follow the same shape: the acl grants SampleRecord per project
   * (inspectors / PM crud, project owner and org-readonly read), conditional
   * on project_id. Org web read-only mode masks every role to project owner,
   * so on the web the tab is a viewer for those orgs.
   */
  canReadSamples(): boolean {
    if (!this.projectId) return false;
    return this.ability.can('read', subject('SampleRecord', { project_id: this.projectId }));
  }

  /**
   * MGVW follows the same shape as diaries: the acl grants MgvwRecord per
   * project (PM / lead inspector / inspector crud, project owner and
   * org-readonly read), conditional on project_id.
   */
  canReadMgvw(): boolean {
    if (!this.projectId) return false;
    return this.ability.can('read', subject('MgvwRecord', { project_id: this.projectId }));
  }

  /** Risk register: the acl grants Risk per project to every project role (spec §4.5). */
  canReadRisks(): boolean {
    if (!this.projectId) return false;
    return this.ability.can('read', subject('Risk', { project_id: this.projectId }));
  }

  /**
   * The pay app, bid sheet and Activity IDs tabs hang off displayActivityCodes,
   * which the parent settles once appdata and the project have loaded (a
   * project can carry its own widget config). The bar is built imperatively,
   * so rebuild it when the input changes after the first pass.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.displayActivityCodes && !changes.displayActivityCodes.firstChange) {
      this.updateHeaderDisplay();
    }
  }

  ngOnInit() {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');

      this.rptService
        .getReports(new Project({ id: this.projectId }), null, { include: ['project', 'organization'] })
        .then((templates: any[]) => {
          if (this.destroyed) return;
          this.formsTabEnabled = (templates ?? []).some(t => {
            try {
              const parsed: any = yaml.load(t.template);
              return parsed?.mapped_report?.enabled === true;
            } catch {
              return false;
            }
          });
          this.updateHeaderDisplay();
        })
        .catch(() => {
          if (this.destroyed) return;
          this.formsTabEnabled = false;
          this.updateHeaderDisplay();
        });

      this.account = this.authService.getCurrentUser();
      this.role = this.account.roles.find(obj => obj.project_id === this.projectId)?.role;

      if (!!this.role?.includes('reviewer')) {
        this.actingRole = 'reviewer';
        this.headersList = this.headersList.filter(obj =>
          ['Submittals', 'RFIs', 'Risk register'].includes(obj.label),
        );
      }

      if (!!this.role?.includes('contractor-basic')) {
        this.actingRole = 'contractor-basic';
        this.headersList = this.headersList.filter(obj =>
          ['Submittals', 'RFIs', 'Warranties & Claims', 'Punch lists', 'Risk register'].includes(obj.label),
        );
      }

      if (!!this.role?.includes('project-manager')) {
        this.actingRole = 'project-manager';
      }
    }

    this.projectsService.actingRole.next(this.actingRole);

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        if (project && Object.keys(project).length) {
          this.project = project;
          this.refreshSamplesEnabled();
          this.changeOrderMode = project?.source_entity_extra_attributes?.change_order_mode || 'direct';
          const coTab = this.headersList.find(h => h.id === 4);
          if (coTab) {
            coTab.label = this.changeOrderMode === 'direct' ? 'Bid sheet' : 'Change orders';
          }
        }
      });

    // Orgs without a `sample_record_general` config (no fields) don't get the
    // Samples tab, and neither do projects that are not opted in when the org
    // rolls sample collection out per project. Same gate the mobile app uses.
    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.appData = data;
        this.mgvwEnabled = !!(data?.dailyReportFields?.contract_mgvw?.fields?.length);
        this.aashtoEnabled = !!data?.enableAashtoIntegration;
        this.refreshSamplesEnabled();
      });

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.diaryDetailMode = isDiaryDetailMode(qp);

        if (qp.fs === undefined) {
          let newTabId: number;
          if (qp.tab) {
            // Whether a tab lives under "More" is its POSITION in headersList
            // (the bar renders `slice:0:4` inline), not its id. Testing the id
            // worked only while ids happened to match positions: a tab added
            // later with a higher id showed inline AND as the More label.
            const visible = this.visibleHeaders;
            const index = visible.findIndex(o => o.qp === qp.tab);
            newTabId = (index >= 0 ? visible[index] : this.headersList.find(o => o.qp === qp.tab))?.id ?? 0;
            this.selectedTabLabel = index >= this.inlineTabCount ? visible[index]?.label : null;
          } else {
            // Default to first tab when no tab query param
            newTabId = this.headersList[0]?.id ?? 0;
          }
          
          // Clear breadcrumbs if switching away from Documents tab (id 7)
          if (this.selectedTabId === 7 && newTabId !== 7) {
            this.store.dispatch(
              UpdateProjectDocumentsBreadcrumbs({
                breadcrumbs: [],
                currentRootId: null,
              })
            );
          }
          
          this.selectedTabId = newTabId;
        }
      });

    this.updateHeaderDisplay();
  }

  selectHeader(header) {
    this.handleTabClicked(header, false);
  }

  selectMenu(header) {
    this.handleTabClicked(header, true);
  }

  handleTabClicked(header: any, isMenu: boolean) {
    this.analyticsService.capture('project_tab_viewed', {
      tab_name: header.analyticsName ?? header.label,
      project_id: this.projectId,
    });
    this.projectsService.closeRightPanel.next();

    // Some dropdown items open a sibling route instead of switching tabs inside
    // this shell (e.g. Offsite tests lives in its own module at /offsite-tests/project/:id).
    if (header.navigateOut && header.qp === 'offsite-tests') {
      this.router.navigate(['/offsite-tests/project/', this.projectId]);
      return;
    }

    // Clear breadcrumbs if switching away from Documents tab (id 7)
    if (this.selectedTabId === 7 && header.id !== 7) {
      this.store.dispatch(
        UpdateProjectDocumentsBreadcrumbs({
          breadcrumbs: [],
          currentRootId: null,
        })
      );
    }

    const leavingRisks = this.selectedTabId === 18 && header.qp !== 'risks';
    const leavingIssues = this.selectedTabId === 17 && header.id !== 17;
    this.selectedTabId = header.id;
    this.selectedTabLabel = isMenu ? header.label : null;

    this.router.navigate([], {
      queryParams: {
        // The Issues tab's own params don't carry over to another tab.
        ...(header.qp !== ISSUES_TAB && Object.fromEntries(ISSUES_TAB_QUERY_PARAMS.map(key => [key, null]))),
        // Nor its status / date filter: tabs that don't reset them (Punchlist
        // reads `status`) would filter on the issue statuses.
        ...(leavingIssues && { status: null, startDate: null, endDate: null }),
        tab: header.qp,
        // Risk register filters are risk-only params; don't carry them into other tabs.
        ...(leavingRisks && RISK_LIST_PARAMS_CLEARED),
        ...(header.qp === ISSUES_TAB && { subTab: null, status: null, startDate: null, endDate: null }),
        ...(header.qp === 'sites' && {
          status: ['submitted-reviewed', 'approved', 'draft'],
          startDate: moment().subtract(15, 'days').format('YYYY-MM-DD'),
          endDate: moment().format('YYYY-MM-DD'),
        }),
        ...(header.qp === 'submittals' && { subTab: 'list', status: null, startDate: null, endDate: null }),
        ...(header.qp === 'mtco' && { subTab: this.changeOrderMode === 'direct' ? 'change-orders' : 'ciwr', status: null, startDate: null, endDate: null }),
        ...(header.qp === 'rfis' && { subTab: null, status: null, startDate: null, endDate: null }),
        ...(header.qp === 'delivery-tracking' && { dtTab: 'bid-items', subTab: null, status: null, startDate: null, endDate: null }),
        ...(header.qp === 'samples' && { subTab: null, status: null, startDate: null, endDate: null, sampleId: null }),
        ...(header.qp === 'mgvw' && { subTab: null, status: null, startDate: null, endDate: null, mgvwId: null }),
        ...(header.qp === 'risks' && { subTab: null, status: null, startDate: null, endDate: null, ...RISK_LIST_PARAMS_CLEARED }),
        ...(header.qp === 'forms' && { subTab: null, status: null, startDate: null, endDate: null }),
        ...(header.qp === 'warranty' && { subTab: 'settings', status: null, startDate: null, endDate: null }),
        ...(header.qp === 'settings' && { subTab: 'accounts', dtTab: null, status: null, startDate: null, endDate: null }),
        ...(header.qp === 'payapp' && { subTab: null, status: null, startDate: null, endDate: null }),
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Always re-evaluates the bar: besides the samples flag, the ability-gated
   * tabs (Diaries, Samples) depend on the rules that /appdata replaces after
   * login, so a flag-only short-circuit would leave them stale.
   */
  private refreshSamplesEnabled() {
    this.samplesEnabled = isSampleCollectionEnabled(this.appData, this.project);
    this.updateHeaderDisplay();
  }

  updateHeaderDisplay() {
    this.headersList = this.headersList.map(header => {
      switch (header.id) {
        case 0: // Daily reports
        case 6: // Consolidated reports
          return { ...header, display: this.actingRole !== 'contractor-basic' && this.actingRole !== 'reviewer' };

        case 1: // Submittals
        case 2: // Rfi's
        case 7: // Document's
          return { ...header, display: true };

        case 15: // Diary
          // Only for AASHTOWare orgs (appdata enableAashtoIntegration) AND
          // users the acl grants DailyDiary on this project. Those rules are
          // conditional on project_id and are registered under both the
          // numeric id and the uuid, so either form matches.
          return { ...header, display: this.aashtoEnabled && this.canReadDiaries() };

        case 8: // Settings
        case 9: // Delivery tracking
        case 10: // Offsite tests (navigate out)
          return { ...header, display: !this.isContractorMode && this.actingRole !== 'contractor-basic' && this.actingRole !== 'reviewer' };

        case 3: // Payapp
          return {
            ...header,
            display: !this.displayActivityCodes && this.actingRole !== 'contractor-basic' && this.actingRole !== 'reviewer',
          };

        case 4: // Change orders
          return {
            ...header,
            display: !this.isContractorMode && !this.displayActivityCodes && this.actingRole !== 'contractor-basic' && this.actingRole !== 'reviewer',
          };

        case 5: // Activity ids
          return {
            ...header,
            display:
              this.currentUser?.roles[0]?.role === 'project-manager' &&
              this.displayActivityCodes &&
              this.actingRole !== 'contractor-basic' &&
              this.actingRole !== 'reviewer',
          };

        case 19: // Drawings — acl grants ProjectDrawing read on this project
          return {
            ...header,
            display: !!this.projectId && this.ability.can('read', subject('ProjectDrawing', { project_id: this.projectId })),
          };

        case 17: // Issues — same check as the old /issues route guard
          return {
            ...header,
            display: this.ability.can('read', 'Issue'),
          };

        case 11: // Warranties & Claims
          return { ...header, display: this.actingRole !== 'reviewer' };

        case 12: // Samples — only show when the org has sample_record_general
          // fields configured (mirrors the mobile gate on appData).
          return {
            ...header,
            display:
              this.samplesEnabled &&
              this.canReadSamples() &&
              this.actingRole !== 'contractor-basic' &&
              this.actingRole !== 'reviewer',
          };

        case 16: // MGVW — org has the contract_mgvw group AND the acl grants
          // MgvwRecord on this project (mirrors the Diaries gate).
          return {
            ...header,
            display:
              this.mgvwEnabled &&
              this.canReadMgvw() &&
              this.actingRole !== 'contractor-basic' &&
              this.actingRole !== 'reviewer',
          };

        case 18: // Risk register — every project, gated only by the Risk acl
          return { ...header, display: this.canReadRisks() };

        case 13: // Punchlist
          return { ...header, display: this.actingRole !== 'reviewer' };
        case 14: // Forms
          // Visible when at least one report template scoped to the project's org
          // has mapped_report.enabled: true. Resolution happens via formsTabEnabled
          // (set by the template fetch in ngOnInit, Task 6).
          return {
            ...header,
            display: this.formsTabEnabled
              && this.actingRole !== 'contractor-basic'
              && this.actingRole !== 'reviewer',
          };

        default:
          return { ...header, display: false };
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    if (this.layoutFrame !== null) cancelAnimationFrame(this.layoutFrame);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
